import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Plus, Search, Eye, Download, Loader2, X, ArrowLeft, CheckCircle, Send, Ban } from 'lucide-react';
import { proformaInvoiceAPI, orderConfirmationAPI, fetchPaged, companySettingsAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import Pagination from '../ui/Pagination';

const statusColors = {
  Generated: 'bg-blue-50 text-blue-700',
  Sent: 'bg-amber-50 text-amber-700',
  Paid: 'bg-green-50 text-green-700',
  Cancelled: 'bg-red-50 text-red-700',
};

const ProformaInvoiceModule = () => {
  const [pis, setPis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const [selectedPI, setSelectedPI] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const [createForm, setCreateForm] = useState({ oc_id: '', pi_date: new Date().toISOString().slice(0, 10), payment_due_date: '', bank_details: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard
  const [ocSearch, setOcSearch] = useState('');
  const [ocResults, setOcResults] = useState([]);
  const [ocSearching, setOcSearching] = useState(false);
  const [selectedOC, setSelectedOC] = useState(null);
  const [showOcDropdown, setShowOcDropdown] = useState(false);
  const ocSearchRef = useRef(null);
  const ocDropdownRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const [logoBase64, setLogoBase64] = useState('');

  // Preload company logo for PDF generation
  useEffect(() => {
    fetch('/prismoline-logo.png')
      .then(r => r.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
  }, []);

  // Fetch company bank details on mount and pre-fill PI form
  useEffect(() => {
    companySettingsAPI.getBankDetails().then(bd => {
      if (bd && (bd.bank_name || bd.account_number)) {
        const lines = [];
        if (bd.account_holder) lines.push(bd.account_holder);
        if (bd.bank_name) lines.push(`Bank: ${bd.bank_name}`);
        if (bd.account_number) lines.push(`A/c No: ${bd.account_number}`);
        if (bd.ifsc_code) lines.push(`IFSC: ${bd.ifsc_code}`);
        if (bd.branch_name) lines.push(`Branch: ${bd.branch_name}`);
        if (bd.upi_id) lines.push(`UPI: ${bd.upi_id}`);
        const bankStr = lines.join('\n');
        setCreateForm(prev => ({ ...prev, bank_details: prev.bank_details || bankStr }));
      }
    }).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      const result = await fetchPaged('/proforma-invoices', params);
      setPis(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, page, pageSize]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // OC search with debounce
  useEffect(() => {
    if (!showCreateDialog) return;
    const timer = setTimeout(async () => {
      try {
        setOcSearching(true);
        const result = await fetchPaged('/proforma-invoices/eligible-ocs', { search: ocSearch, page: 1, page_size: 20 });
        setOcResults(result.items || []);
        setShowOcDropdown(true);
      } catch (err) { setOcResults([]); }
      finally { setOcSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [ocSearch, showCreateDialog]);

  // Close OC dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ocDropdownRef.current && !ocDropdownRef.current.contains(e.target) &&
          ocSearchRef.current && !ocSearchRef.current.contains(e.target)) {
        setShowOcDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOcSelect = (oc) => {
    setSelectedOC(oc);
    setCreateForm(prev => ({ ...prev, oc_id: oc.id }));
    setOcSearch(oc.oc_number);
    setShowOcDropdown(false);
  };

  const handleOcClear = () => {
    setSelectedOC(null);
    setCreateForm(prev => ({ ...prev, oc_id: '' }));
    setOcSearch('');
  };

  const filteredPIs = pis;

  const handleCreate = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate PIs
    if (!createForm.oc_id) { toast.error('Please select an Order Confirmation'); return; }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      const result = await proformaInvoiceAPI.create(createForm);
      toast.success(`Proforma Invoice ${result.pi_number} generated`);
      setShowCreateDialog(false);
      setCreateForm({ oc_id: '', pi_date: new Date().toISOString().slice(0, 10), payment_due_date: '', bank_details: createForm.bank_details, notes: '' });
      setSelectedOC(null);
      setOcSearch('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create PI');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleStatusUpdate = async (piId, newStatus) => {
    try {
      await proformaInvoiceAPI.updateStatus(piId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchAll();
      if (selectedPI?.id === piId) {
        const updated = await proformaInvoiceAPI.getById(piId);
        setSelectedPI(updated);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const generatePDF = (pi) => {
    const pdf = new jsPDF();
    const pw = 210, m = 15;
    let y = 15;

    // Header — matches PO style with salesperson contact + logo
    const spPhone = pi.salesman_phone || '';
    const contactLine = `www.prismoline.com | sales@prismoline.com${spPhone ? ' | +91 ' + spPhone : ''}`;
    pdf.setFillColor(36, 84, 156);
    pdf.rect(0, 0, pw, 38, 'F');
    pdf.setTextColor(255); pdf.setFontSize(18); pdf.setFont(undefined, 'bold');
    pdf.text('SHARDA INFRASOLUTIONS PVT. LTD.', m, 15);
    pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
    pdf.text('Road Safety & Infrastructure Solutions', m, 22);
    pdf.text('CIN: U24100JH2010PTC014546 | PAN: AAPCS1562H', m, 28);
    pdf.text(contactLine, m, 34);
    if (logoBase64) {
      try { pdf.addImage(logoBase64, 'PNG', pw - m - 57, 2, 57, 32); } catch (e) { /* logo failed silently */ }
    }

    y = 45;
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
    pdf.text('PROFORMA INVOICE', pw / 2, y, { align: 'center' });
    y = 50; pdf.setDrawColor(36, 84, 156); pdf.line(m, y, pw - m, y);
    y += 8;

    // PI details
    pdf.setTextColor(100); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
    pdf.text(`PI No: ${pi.pi_number}`, m, y);
    pdf.text(`Date: ${pi.pi_date}`, pw - m - 40, y);
    y += 5;
    pdf.text(pi.source === 'quotation' ? `Quotation Ref: ${pi.quotation_number}` : `OC Ref: ${pi.oc_number}`, m, y);
    if (pi.payment_due_date) pdf.text(`Due: ${pi.payment_due_date}`, pw - m - 40, y);
    y += 8;

    // Bill To / Ship From — 2-box layout with dynamic height (no overlap)
    const halfW = (pw - 2 * m - 4) / 2;

    // Bill To content prep
    const billAddrLines = pi.customer_address ? pdf.splitTextToSize(pi.customer_address, halfW - 6) : [];
    pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    const custNameLines = pdf.splitTextToSize(pi.customer_name || '', halfW - 6);
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let billBoxH = 16 + (custNameLines.length - 1) * 4 + billAddrLines.length * 3.5 + (pi.customer_gstin ? 5 : 0);

    // Ship From content prep
    const ba = pi.billing_address || {};
    const sfBranch = ba.name || ba.branch_name || '';
    const sfAddrParts = [ba.address, ba.city, ba.state, ba.pincode ? `- ${ba.pincode}` : ''].filter(Boolean).join(', ');
    const sfAddrLines = sfAddrParts ? pdf.splitTextToSize(sfAddrParts, halfW - 6) : [];
    let sfBoxH = 16 + (sfBranch ? 5 : 0) + sfAddrLines.length * 3.5 + (ba.gstin ? 5 : 0);

    const bxH = Math.max(billBoxH, sfBoxH, 28) + 4;

    // Box 1: BILL TO
    pdf.setFillColor(245, 247, 250); pdf.rect(m, y, halfW, bxH, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
    pdf.text('BILL TO:', m + 3, y + 5);
    pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    custNameLines.forEach((l, i) => pdf.text(l, m + 3, y + 11 + i * 4));
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let bY = y + 16 + (custNameLines.length - 1) * 4;
    billAddrLines.forEach(l => { pdf.text(l, m + 3, bY); bY += 3.5; });
    if (pi.customer_gstin) { pdf.text(`GSTIN: ${pi.customer_gstin}`, m + 3, bY + 1); }

    // Box 2: SHIP FROM (branch)
    const box2X = m + halfW + 4;
    pdf.setFillColor(245, 247, 250); pdf.rect(box2X, y, halfW, bxH, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
    pdf.text('SHIP FROM:', box2X + 3, y + 5);
    pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    pdf.text('Sharda Infrasolutions Pvt Ltd', box2X + 3, y + 11);
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let sY = y + 16;
    if (sfBranch) { pdf.setFont(undefined, 'bold'); pdf.setFontSize(7.5); pdf.text(sfBranch, box2X + 3, sY); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7); sY += 4; }
    sfAddrLines.forEach(l => { pdf.text(l, box2X + 3, sY); sY += 3.5; });
    if (ba.gstin) { pdf.text(`GSTIN: ${ba.gstin}`, box2X + 3, sY + 1); }
    y += bxH + 4;

    // Optional: Ship To (customer delivery)
    if (pi.ship_to && Object.values(pi.ship_to).some(v => v)) {
      const stAddrParts = [pi.ship_to.address, pi.ship_to.city, pi.ship_to.state, pi.ship_to.pincode].filter(Boolean).join(', ');
      const stAddrLines = stAddrParts ? pdf.splitTextToSize(stAddrParts, pw - 2 * m - 6) : [];
      const stH = 14 + Math.max(stAddrLines.length * 3.5, 4);
      pdf.setFillColor(250, 250, 252); pdf.rect(m, y, pw - 2 * m, stH, 'F');
      pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
      pdf.text('SHIP TO (DELIVERY ADDRESS):', m + 3, y + 5);
      if (pi.ship_to.gstin) {
        pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
        pdf.text(`GSTIN: ${pi.ship_to.gstin}`, pw - m - 3, y + 5, { align: 'right' });
      }
      // Name on its own line below the heading
      pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
      pdf.text(pi.ship_to.name || '', m + 3, y + 10);
      // Address lines stacked below the name (NOT same line as name)
      pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
      let stY = y + 14;
      stAddrLines.forEach(l => { pdf.text(l, m + 3, stY); stY += 3.5; });
      y += stH + 4;
    }

    // Items table with grid
    const tableX = m;
    const tableW = pw - 2 * m;
    const colPositions = [tableX, tableX + 8, tableX + 68, tableX + 98, tableX + 118, tableX + 145, tableX + tableW];
    const colHeaders = ['#', 'Description', 'HSN', 'Qty', 'Rate', 'Amount'];
    const headerH = 7;

    pdf.setFillColor(36, 84, 156);
    pdf.rect(tableX, y, tableW, headerH, 'F');
    pdf.setTextColor(255); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    colHeaders.forEach((h, i) => pdf.text(h, colPositions[i] + 2, y + 5));
    y += headerH;

    pdf.setFont(undefined, 'normal'); pdf.setTextColor(0); pdf.setFontSize(7.5);
    (pi.items || []).forEach((item, i) => {
      // Wrap long product names into multiple lines
      const nameLines = pdf.splitTextToSize(item.product_name || '', 58);
      // Also wrap description if present
      const descLines = item.description ? pdf.splitTextToSize(item.description, 58) : [];
      const totalTextLines = nameLines.length + descLines.length;
      const rowH = Math.max(6, totalTextLines * 3.5 + 2);
      if (y + rowH > 260) { pdf.addPage(); y = 20; }
      if (i % 2 === 0) { pdf.setFillColor(248, 249, 252); pdf.rect(tableX, y, tableW, rowH, 'F'); }
      pdf.setDrawColor(220); pdf.line(tableX, y + rowH, tableX + tableW, y + rowH);
      pdf.setTextColor(0);
      pdf.text(`${i + 1}`, colPositions[0] + 2, y + 4.5);
      // Draw product name
      pdf.setFont(undefined, 'bold');
      nameLines.forEach((ln, k) => pdf.text(ln, colPositions[1] + 2, y + 4.5 + k * 3.5));
      // Draw description below name in lighter color
      if (descLines.length > 0) {
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(100);
        descLines.forEach((ln, k) => pdf.text(ln, colPositions[1] + 2, y + 4.5 + (nameLines.length + k) * 3.5));
        pdf.setTextColor(0);
      }
      pdf.setFont(undefined, 'normal');
      pdf.text(item.hsn_code || '', colPositions[2] + 2, y + 4.5);
      pdf.text(`${item.qty} ${item.unit}`, colPositions[3] + 2, y + 4.5);
      pdf.text(`${item.rate?.toLocaleString('en-IN')}`, colPositions[4] + 2, y + 4.5);
      pdf.text(`${item.amount?.toLocaleString('en-IN')}`, colPositions[5] + 2, y + 4.5);
      y += rowH;
    });
    pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.5); pdf.line(tableX, y, tableX + tableW, y); pdf.setLineWidth(0.2);

    y += 6;

    // Totals
    const rx = pw - m - 60;
    pdf.setFontSize(8);
    pdf.text('Subtotal:', rx, y); pdf.setFont(undefined, 'bold');
    pdf.text(`INR ${pi.subtotal?.toLocaleString('en-IN')}`, rx + 40, y); y += 5;
    pdf.setFont(undefined, 'normal');

    // Other charges (freight, packing, etc) — between subtotal and GST
    (pi.other_charges || []).forEach(ch => {
      const amt = Number(ch.amount) || 0;
      if (!amt) return;
      const label = `${ch.description || 'Other Charge'}${ch.taxable !== false ? ' (+GST)' : ''}:`;
      pdf.text(label, rx, y); pdf.text(`INR ${amt.toLocaleString('en-IN')}`, rx + 40, y); y += 5;
    });

    if (pi.cgst) { pdf.text(`CGST (${pi.gst_rate / 2}%):`, rx, y); pdf.text(`INR ${pi.cgst?.toLocaleString('en-IN')}`, rx + 40, y); y += 5; }
    if (pi.sgst) { pdf.text(`SGST (${pi.gst_rate / 2}%):`, rx, y); pdf.text(`INR ${pi.sgst?.toLocaleString('en-IN')}`, rx + 40, y); y += 5; }
    if (pi.igst) { pdf.text(`IGST (${pi.gst_rate}%):`, rx, y); pdf.text(`INR ${pi.igst?.toLocaleString('en-IN')}`, rx + 40, y); y += 5; }
    if (!pi.cgst && !pi.igst) { pdf.text(`GST (${pi.gst_rate}%):`, rx, y); pdf.text(`INR ${pi.gst_amount?.toLocaleString('en-IN')}`, rx + 40, y); y += 5; }

    pdf.setFontSize(10); pdf.setFont(undefined, 'bold');
    pdf.text('TOTAL:', rx, y); pdf.text(`INR ${pi.total?.toLocaleString('en-IN')}`, rx + 40, y);
    y += 10;

    // Terms & Conditions FIRST (above bank details)
    if (pi.terms) {
      if (y > 240) { pdf.addPage(); y = 20; }
      pdf.setFont(undefined, 'bold'); pdf.setTextColor(36, 84, 156); pdf.setFontSize(8);
      pdf.text('Terms & Conditions:', m, y); y += 5;
      pdf.setFont(undefined, 'normal'); pdf.setTextColor(80); pdf.setFontSize(7);
      const termsText = typeof pi.terms === 'string'
        ? pi.terms
        : [
            pi.terms.payment_terms && `Payment Terms: ${pi.terms.payment_terms}`,
            pi.terms.delivery_schedule && `Delivery: ${pi.terms.delivery_schedule}`,
            pi.terms.warranty && `Warranty: ${pi.terms.warranty}`,
            pi.terms.freight && `Freight: ${pi.terms.freight}`,
            pi.terms.notes && `Notes: ${pi.terms.notes}`,
          ].filter(Boolean).join('\n');
      const termsLines = pdf.splitTextToSize(termsText, pw - 2 * m);
      termsLines.forEach(line => {
        if (y > 275) { pdf.addPage(); y = 20; }
        pdf.text(line, m, y); y += 3.5;
      });
      y += 4;
    }

    // Bank Details AFTER terms
    if (pi.bank_details) {
      if (y > 255) { pdf.addPage(); y = 20; }
      pdf.setFontSize(8); pdf.setFont(undefined, 'bold'); pdf.setTextColor(36, 84, 156);
      pdf.text('Bank Details:', m, y); y += 5;
      pdf.setFont(undefined, 'normal'); pdf.setTextColor(0); pdf.setFontSize(7.5);
      pi.bank_details.split('\n').forEach(line => {
        if (y > 280) { pdf.addPage(); y = 20; }
        pdf.text(line, m, y); y += 4;
      });
    }

    if (pi.notes) {
      if (y > 265) { pdf.addPage(); y = 20; }
      y += 3;
      pdf.setFont(undefined, 'bold'); pdf.setTextColor(36, 84, 156); pdf.setFontSize(8);
      pdf.text('Notes:', m, y); y += 5;
      pdf.setFont(undefined, 'normal'); pdf.setTextColor(0); pdf.setFontSize(7.5);
      const notesLines = pdf.splitTextToSize(pi.notes, pw - 2 * m);
      notesLines.forEach(ln => { pdf.text(ln, m, y); y += 4; });
    }

    pdf.save(`${pi.pi_number}.pdf`);
  };

  // Detail View
  if (selectedPI) {
    const pi = selectedPI;
    return (
      <div data-testid="pi-detail-view">
        <button onClick={() => setSelectedPI(null)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-4" data-testid="pi-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to list
        </button>
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{pi.pi_number}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{pi.source === 'quotation' ? `Quotation: ${pi.quotation_number}` : `OC: ${pi.oc_number}`} | {pi.pi_date}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full ${statusColors[pi.status] || ''}`}>{pi.status}</span>
              <button onClick={() => generatePDF(pi)} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }} data-testid="pi-download-pdf">
                <Download className="w-3 h-3" /> PDF
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs mb-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-500 mb-1">Customer</p>
              <p className="font-semibold">{pi.customer_name}</p>
              <p className="text-gray-500 mt-1">{pi.customer_address}</p>
              {pi.customer_gstin && <p className="text-gray-400 mt-0.5">GSTIN: {pi.customer_gstin}</p>}
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-500 mb-1">Payment</p>
              <p className="font-bold text-lg" style={{ color: '#24549c' }}>INR {pi.total?.toLocaleString('en-IN')}</p>
              {pi.payment_due_date && <p className="text-gray-500 mt-1">Due: {pi.payment_due_date}</p>}
            </div>
          </div>

          {/* Items */}
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="text-left p-2 rounded-l-lg">#</th>
                <th className="text-left p-2">SKU</th>
                <th className="text-left p-2">Item</th>
                <th className="text-left p-2">HSN</th>
                <th className="text-right p-2">Qty</th>
                <th className="text-right p-2">Rate</th>
                <th className="text-right p-2 rounded-r-lg">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(pi.items || []).map((item, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="p-2 text-gray-400">{i + 1}</td>
                  <td className="p-2 text-gray-500 font-mono text-[10px]">{item.sku || '-'}</td>
                  <td className="p-2">
                    <span className="font-medium">{item.product_name}</span>
                    {item.description && (
                      <p className="text-[10px] text-gray-500 mt-0.5">{item.description}</p>
                    )}
                  </td>
                  <td className="p-2 text-gray-500">{item.hsn_code}</td>
                  <td className="p-2 text-right">{item.qty} {item.unit}</td>
                  <td className="p-2 text-right">{item.rate?.toLocaleString('en-IN')}</td>
                  <td className="p-2 text-right font-semibold">{item.amount?.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mb-4">
            <div className="text-xs space-y-1 text-right">
              <p className="text-gray-500">Subtotal: <span className="font-semibold text-gray-900">INR {pi.subtotal?.toLocaleString('en-IN')}</span></p>
              <p className="text-gray-500">GST ({pi.gst_rate}%): <span className="font-semibold text-gray-900">INR {pi.gst_amount?.toLocaleString('en-IN')}</span></p>
              <p className="text-sm font-bold" style={{ color: '#24549c' }}>Total: INR {pi.total?.toLocaleString('en-IN')}</p>
            </div>
          </div>

          {pi.bank_details && (
            <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 text-xs mb-3">
              <p className="font-semibold text-blue-700 mb-1">Bank Details</p>
              <pre className="text-gray-600 whitespace-pre-wrap font-sans">{pi.bank_details}</pre>
            </div>
          )}

          {pi.terms && (typeof pi.terms === 'string' ? pi.terms : Object.values(pi.terms).some(v => v)) && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs mb-3">
              <p className="font-semibold text-gray-700 mb-1">Terms & Conditions</p>
              <pre className="text-gray-600 whitespace-pre-wrap font-sans text-[11px]">
                {typeof pi.terms === 'string' 
                  ? pi.terms 
                  : [
                      pi.terms.payment_terms && `Payment Terms: ${pi.terms.payment_terms}`,
                      pi.terms.delivery_schedule && `Delivery: ${pi.terms.delivery_schedule}`,
                      pi.terms.warranty && `Warranty: ${pi.terms.warranty}`,
                      pi.terms.freight && `Freight: ${pi.terms.freight}`,
                      pi.terms.notes && `Notes: ${pi.terms.notes}`,
                    ].filter(Boolean).join('\n')
                }
              </pre>
            </div>
          )}

          {/* Status Actions */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
            {pi.status === 'Generated' && (
              <button onClick={() => handleStatusUpdate(pi.id, 'Sent')} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100" data-testid="pi-mark-sent">
                <Send className="w-3 h-3" /> Mark as Sent
              </button>
            )}
            {['Generated', 'Sent'].includes(pi.status) && (
              <button onClick={() => handleStatusUpdate(pi.id, 'Paid')} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold bg-green-50 text-green-700 rounded-lg hover:bg-green-100" data-testid="pi-mark-paid">
                <CheckCircle className="w-3 h-3" /> Mark as Paid
              </button>
            )}
            {pi.status !== 'Cancelled' && pi.status !== 'Paid' && (
              <button onClick={() => handleStatusUpdate(pi.id, 'Cancelled')} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold bg-red-50 text-red-600 rounded-lg hover:bg-red-100" data-testid="pi-cancel">
                <Ban className="w-3 h-3" /> Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="proforma-invoice-module">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search PIs..."
            data-testid="pi-search"
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <button onClick={() => setShowCreateDialog(true)} data-testid="create-pi-btn"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200"
          style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> Generate Proforma Invoice
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-500">Loading...</span>
        </div>
      ) : filteredPIs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <FileText className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">No Proforma Invoices yet</p>
          <p className="text-xs text-gray-400 mt-1">Generate one from an Order Confirmation or Quotation</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="text-left p-3">PI Number</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Customer</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPIs.map(pi => (
                <tr key={pi.id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`pi-row-${pi.id}`}>
                  <td className="p-3 font-semibold text-gray-900">{pi.pi_number}</td>
                  <td className="p-3 text-gray-500">{pi.source === 'quotation' ? pi.quotation_number : pi.oc_number}</td>
                  <td className="p-3">{pi.customer_name}</td>
                  <td className="p-3 text-right font-bold" style={{ color: '#24549c' }}>INR {pi.total?.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-gray-500">{pi.pi_date}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[pi.status] || ''}`}>{pi.status}</span></td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setSelectedPI(pi)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid={`pi-view-${pi.id}`}>
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                      <button onClick={() => generatePDF(pi)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid={`pi-pdf-${pi.id}`}>
                        <Download className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pis.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="create-pi-dialog">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Generate Proforma Invoice</h2>
              <button onClick={() => setShowCreateDialog(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="relative">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Search Order Confirmation *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input ref={ocSearchRef} value={ocSearch}
                    onChange={e => { setOcSearch(e.target.value); if (selectedOC) { setSelectedOC(null); setCreateForm(prev => ({ ...prev, oc_id: '' })); } }}
                    onFocus={() => setShowOcDropdown(true)}
                    placeholder="Type OC number or customer name..."
                    data-testid="pi-oc-search"
                    className="w-full pl-9 pr-8 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                  {(ocSearch || selectedOC) && (
                    <button onClick={handleOcClear} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100">
                      <X className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  )}
                </div>
                {showOcDropdown && !selectedOC && (
                  <div ref={ocDropdownRef} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[250px] overflow-y-auto">
                    {ocSearching ? (
                      <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...
                      </div>
                    ) : ocResults.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-gray-400">No eligible OCs found</div>
                    ) : (
                      ocResults.map(oc => (
                        <button key={oc.id} onClick={() => handleOcSelect(oc)}
                          data-testid={`oc-option-${oc.id}`}
                          className="w-full text-left px-4 py-2.5 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">{oc.oc_number}</span>
                            <span className="font-bold" style={{ color: '#24549c' }}>INR {oc.total?.toLocaleString('en-IN')}</span>
                          </div>
                          <p className="text-[10px] text-gray-500 mt-0.5">{oc.customer?.name} | {oc.status}</p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {selectedOC && (
                <div className="p-3 bg-blue-50/40 rounded-lg border border-blue-100 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-gray-900">{selectedOC.oc_number}</span>
                    <span className="font-bold" style={{ color: '#24549c' }}>INR {selectedOC.total?.toLocaleString('en-IN')}</span>
                  </div>
                  <p className="text-gray-600">{selectedOC.customer?.name}</p>
                  {selectedOC.terms && (typeof selectedOC.terms === 'string' ? selectedOC.terms : Object.values(selectedOC.terms).some(v => v)) && (
                    <div className="mt-2 pt-2 border-t border-blue-100">
                      <p className="text-[10px] font-semibold text-blue-700 mb-0.5">Terms (auto-fetched from OC)</p>
                      <p className="text-[10px] text-gray-500 line-clamp-3 whitespace-pre-line">
                        {typeof selectedOC.terms === 'string' 
                          ? selectedOC.terms 
                          : [
                              selectedOC.terms.payment_terms && `Payment: ${selectedOC.terms.payment_terms}`,
                              selectedOC.terms.delivery_schedule && `Delivery: ${selectedOC.terms.delivery_schedule}`,
                              selectedOC.terms.warranty && `Warranty: ${selectedOC.terms.warranty}`,
                              selectedOC.terms.freight && `Freight: ${selectedOC.terms.freight}`,
                            ].filter(Boolean).join(' | ')
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">PI Date</label>
                  <input type="date" value={createForm.pi_date} onChange={e => setCreateForm({ ...createForm, pi_date: e.target.value })}
                    data-testid="pi-date-input"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Payment Due Date</label>
                  <input type="date" value={createForm.payment_due_date} onChange={e => setCreateForm({ ...createForm, payment_due_date: e.target.value })}
                    data-testid="pi-due-date-input"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Bank Details</label>
                <textarea value={createForm.bank_details} onChange={e => setCreateForm({ ...createForm, bank_details: e.target.value })} rows={3}
                  data-testid="pi-bank-input"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Notes</label>
                <input value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })}
                  data-testid="pi-notes-input"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowCreateDialog(false)} disabled={submitting} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !createForm.oc_id} data-testid="pi-submit-btn"
                className="px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#24549c' }}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                Generate PI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProformaInvoiceModule;
